/**
 * Self-contained receipt styles for react-to-print's pageStyle prop.
 *
 * With ignoreGlobalStyles: true, the print iframe gets ONLY these styles.
 * This means no global CSS can ever interfere with receipt printing —
 * not even `body * { visibility: hidden }` from another feature.
 *
 * Includes:
 * - Google Fonts import (Roboto Mono)
 * - All .thermal-receipt screen styles (duplicated from ThermalReceipt.css)
 * - @media print overrides for 58mm thermal printers
 */
export const RECEIPT_PRINT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;600;700&display=swap');

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    margin: 0;
    padding: 0;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .thermal-receipt {
    width: 240px;
    max-width: 100%;
    padding: 12px 14px 18px;
    margin: 0 auto;
    font-family: 'Roboto Mono', 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 600;
    background-color: #fff;
    color: #111;
  }

  .thermal-receipt__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.35rem;
  }

  .thermal-receipt__logo {
    max-width: 100px;
    width: 65%;
    height: auto;
  }

  .thermal-receipt__title {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 1rem;
    text-align: center;
    margin: 0;
    font-weight: 700;
  }

  .thermal-receipt__subtitle {
    text-align: center;
    margin: 0;
    font-size: 0.9rem;
  }

  .thermal-receipt__heading-copy {
    width: 100%;
  }

  .thermal-receipt__meta {
    margin: 0 0 0.4rem;
    padding: 0;
  }

  .thermal-receipt__meta-row {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    margin-bottom: 0.2rem;
  }

  .thermal-receipt__meta-row dt,
  .thermal-receipt__meta-row dd {
    margin: 0;
  }

  .thermal-receipt__meta-row dd {
    font-weight: 600;
  }

  .thermal-receipt__body {
    margin-top: 0.5rem;
  }

  .thermal-receipt__section {
    padding-bottom: 0.4rem;
  }

  .thermal-receipt__section--padded {
    margin-top: 0.4rem;
  }

  .thermal-receipt__section-title {
    margin: 0 0 0.3rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.8rem;
  }

  .thermal-receipt__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.1rem 0;
  }

  .thermal-receipt__row--emphasize {
    font-weight: 700;
  }

  .thermal-receipt__label {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .thermal-receipt__value {
    font-size: 0.85rem;
    text-align: right;
    word-break: break-word;
    font-weight: 700;
  }

  .thermal-receipt__value input,
  .thermal-receipt__value select,
  .thermal-receipt__value textarea {
    width: 100%;
    font-family: inherit;
    font-size: 0.85rem;
  }

  .thermal-receipt__value img {
    max-width: 100%;
    display: block;
    margin-top: 0.2rem;
  }

  .thermal-receipt__separator {
    border-bottom: 1px dashed #c0c0c0;
    margin: 0.4rem 0;
  }

  .thermal-receipt__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .thermal-receipt__list-item {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.15rem 0;
    font-size: 0.85rem;
  }

  .thermal-receipt__empty {
    margin: 0;
    font-size: 0.8rem;
    font-style: italic;
  }

  .thermal-receipt__footer {
    text-align: center;
    font-size: 0.75rem;
    margin: 0.4rem 0 0;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  @media print {
    @page {
      margin: 0mm;
      size: auto;
    }

    body {
      margin: 0;
      padding: 0;
    }

    .thermal-receipt {
      width: 48mm;
      margin: 0;
      margin-left: 3mm;
      padding: 2mm 1mm 3mm 1mm;
      box-shadow: none;
      border-radius: 0;
      border: none;
      overflow: hidden;
    }

    .thermal-receipt__logo {
      max-width: 25mm;
      width: 50%;
    }
  }
`;
